import { createFileRoute, useNavigate, redirect, isRedirect } from "@tanstack/react-router";
import { useState, FormEvent } from "react";
import { Lock, Eye, EyeOff, LogIn, CheckCircle2 } from "lucide-react";
import { changePassword, isAuthenticated } from "@/integrations/api/auth";
import { currentUserQueryOptions } from "@/hooks/useCurrentUser";
import { useQueryClient } from "@tanstack/react-query";


export const Route = createFileRoute("/change-password")({
  ssr: false,
  beforeLoad: async ({ context }) => {
    if (!isAuthenticated()) {
      throw redirect({ to: "/auth" });
    }
    try {
      // Use ensureQueryData — reads from cache if data is fresh, fetches otherwise.
      // On first login the data will already be in cache from auth.tsx login handler.
      const me = await context.queryClient.ensureQueryData(currentUserQueryOptions());

      if (!me.must_change_password) {
        throw redirect({ to: "/" });
      }
    } catch (err) {
      if (isRedirect(err)) throw err;
      throw redirect({ to: "/auth" });
    }
  },
  component: ChangePasswordPage,
});


const MONO = { fontFamily: '"JetBrains Mono", ui-monospace, monospace' };

function ChangePasswordPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const criteria = {
    length: newPassword.length >= 8,
    upperLower: /[A-Z]/.test(newPassword) && /[a-z]/.test(newPassword),
    number: /\d/.test(newPassword),
    special: /[^A-Za-z0-9]/.test(newPassword),
  };

  const isCriteriaMet = Object.values(criteria).every(Boolean);
  const isMatch = newPassword === confirmPassword && newPassword !== "";

  const canSubmit = isCriteriaMet && isMatch && !busy;

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;

    setBusy(true);
    setErr(null);
    try {
      const me = await changePassword(newPassword, confirmPassword);
      // Update the TanStack Query cache directly with the fresh profile before navigating
      queryClient.setQueryData(["current-user"], me);
      // Change successful, we can navigate to dashboard
      navigate({ to: "/" });
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : "Something went wrong while updating password");
      setBusy(false);
    }
  }

  function renderCheck(label: string, valid: boolean) {
    return (
      <div className={`flex items-center gap-2 text-sm ${valid ? "text-[color:var(--mint)]" : "text-muted-foreground"}`}>
        <CheckCircle2 className={`h-4 w-4 ${valid ? "text-[color:var(--mint)]" : "text-muted-foreground/30"}`} />
        <span>{label}</span>
      </div>
    );
  }

  return (
    <div
      className="grid min-h-screen place-items-center bg-surface-soft px-4 py-10"
      style={{ fontFamily: '"Inter", ui-sans-serif, system-ui, sans-serif' }}
    >
      <div className="w-full max-w-md">
        <div className="overflow-hidden rounded-2xl border border-border bg-card p-8 shadow-[0_20px_60px_-20px_rgba(11,22,40,0.25)] sm:p-10">
          <h1 className="text-2xl font-semibold tracking-tight text-foreground">
            Change Your Password
          </h1>
          <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
            For security, you must create a new password before continuing to HAMS.
          </p>

          <form className="mt-8 space-y-6" onSubmit={submit}>
            <div>
              <label
                htmlFor="new-password"
                className="block text-[11px] font-semibold uppercase tracking-wider text-muted-foreground"
                style={MONO}
              >
                New Password
              </label>
              <div className="relative mt-2">
                <Lock className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <input
                  id="new-password"
                  type={showNewPassword ? "text" : "password"}
                  autoComplete="new-password"
                  required
                  placeholder="Enter new password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="h-12 w-full rounded-lg border border-border bg-background pl-10 pr-11 text-sm text-foreground outline-none transition focus:border-primary focus:ring-2 focus:ring-[color:var(--mint)]/40"
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword((v) => !v)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-2 text-muted-foreground hover:bg-secondary hover:text-foreground"
                >
                  {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <div>
              <label
                htmlFor="confirm-password"
                className="block text-[11px] font-semibold uppercase tracking-wider text-muted-foreground"
                style={MONO}
              >
                Confirm New Password
              </label>
              <div className="relative mt-2">
                <Lock className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <input
                  id="confirm-password"
                  type={showConfirmPassword ? "text" : "password"}
                  autoComplete="new-password"
                  required
                  placeholder="Confirm new password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="h-12 w-full rounded-lg border border-border bg-background pl-10 pr-11 text-sm text-foreground outline-none transition focus:border-primary focus:ring-2 focus:ring-[color:var(--mint)]/40"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword((v) => !v)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-2 text-muted-foreground hover:bg-secondary hover:text-foreground"
                >
                  {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              {confirmPassword.length > 0 && !isMatch && (
                <p className="mt-2 text-xs text-destructive">Passwords do not match</p>
              )}
            </div>

            <div className="space-y-2.5 rounded-lg border border-border bg-secondary/50 p-4">
              {renderCheck("Minimum 8 characters", criteria.length)}
              {renderCheck("Uppercase and lowercase letters", criteria.upperLower)}
              {renderCheck("At least one number", criteria.number)}
              {renderCheck("At least one special character", criteria.special)}
            </div>

            {err && (
              <div className="rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2.5 text-sm text-destructive">
                {err}
              </div>
            )}

            <button
              type="submit"
              disabled={!canSubmit}
              className="inline-flex h-12 w-full items-center justify-center gap-2 rounded-lg bg-primary text-sm font-semibold text-primary-foreground shadow-sm transition hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <LogIn className="h-4 w-4" />
              {busy ? "Updating..." : "Set New Password"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
